import React from 'react'
import { useDesignStore } from '../../../store/designStore'
import * as THREE from 'three'

// Enhanced material configurations for realistic rendering
export const getRealisticMaterial = (materialType: string, baseColor: string = '#ffffff') => {
  const { canvasSettings } = useDesignStore.getState()
  
  if (!canvasSettings.enableRealisticMode) {
    // Return basic material for performance
    return <meshStandardMaterial color={baseColor} />
  }

  // Realistic material configurations
  switch (materialType) {
    case 'concrete':
      return (
        <meshStandardMaterial
          color="#c0c0c0"
          roughness={0.9}
          metalness={0.0}
        />
      )
    
    case 'wood':
      return (
        <meshStandardMaterial
          color="#8B4513"
          roughness={0.8}
          metalness={0.0}
        />
      )
    
    case 'metal':
      return (
        <meshStandardMaterial
          color="#B0C4DE"
          roughness={0.2}
          metalness={0.9}
          envMapIntensity={1.0}
        />
      )
    
    case 'glass':
      return (
        <meshPhysicalMaterial
          color="#ffffff"
          transmission={0.9}
          opacity={0.1}
          transparent
          roughness={0.0}
          metalness={0.0}
          clearcoat={1.0}
          clearcoatRoughness={0.0}
        />
      )
    
    case 'water':
      return (
        <meshPhysicalMaterial
          color="#006994"
          transmission={0.8}
          opacity={0.8}
          transparent
          roughness={0.1}
          metalness={0.0}
          clearcoat={1.0}
          clearcoatRoughness={0.1}
          ior={1.33} // Index of refraction for water
        />
      )
    
    case 'grass':
      return (
        <meshStandardMaterial
          color="#90EE90"
          roughness={0.9}
          metalness={0.0}
        />
      )
    
    case 'roof':
      return (
        <meshStandardMaterial
          color="#8B4513"
          roughness={0.7}
          metalness={0.1}
        />
      )
    
    case 'siding':
      return (
        <meshStandardMaterial
          color={baseColor}
          roughness={0.6}
          metalness={0.0}
        />
      )
    
    default:
      return (
        <meshStandardMaterial
          color={baseColor}
          roughness={0.5}
          metalness={0.0}
        />
      )
  }
}

// Component for applying realistic ground material
export const RealisticGroundMaterial: React.FC<{ hasBackgroundImage?: boolean }> = ({ 
  hasBackgroundImage = false 
}) => {
  const { canvasSettings } = useDesignStore()
  
  if (!canvasSettings.enableRealisticMode) {
    return (
      <meshStandardMaterial 
        color="#90EE90" 
        transparent={hasBackgroundImage}
        opacity={hasBackgroundImage ? 0.3 : 1.0}
      />
    )
  }

  return (
    <meshStandardMaterial
      color={hasBackgroundImage ? "#90EE90" : "#7CB342"}
      roughness={0.9}
      metalness={0.0}
      transparent={hasBackgroundImage}
      opacity={hasBackgroundImage ? 0.2 : 1.0}
    />
  )
}

export default { getRealisticMaterial, RealisticGroundMaterial }
